import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import type { ActivityType } from '@movmania/core'
import { ACTIVITY_TYPES, estimateKcal, intensityOf } from '@movmania/core'
import {
  Button,
  IconButton,
  Pill,
  Screen,
  ScreenBody,
  ScreenFooter,
} from '../components/Ui.js'
import { ActivityTileGrid } from '../components/ActivityTile.js'
import { photo } from '../lib/photos.js'
import { useT } from '../lib/useT.js'
import { useStore } from '../store/useStore.js'
import type { LogState } from './Home.js'

const DURATIONS = [15, 20, 30, 45, 60, 90]
const WHEN = [0, 1, 2]

export function LogActivity() {
  const t = useT()
  const navigate = useNavigate()
  const preset = (useLocation().state as LogState | null)?.type
  const addActivity = useStore((s) => s.addActivity)
  const favorites = useStore((s) => s.profile?.favoriteActivities ?? [])
  const weightKg = useStore((s) => s.profile?.weightKg)

  // Les activités déclarées à l'inscription passent devant : moins de taps
  // pour la séance la plus probable.
  const ordered = useMemo(
    () => ({
      favorites,
      rest: ACTIVITY_TYPES.filter((type) => !favorites.includes(type)),
    }),
    [favorites],
  )

  const [type, setType] = useState<ActivityType>(preset ?? favorites[0] ?? 'walk')
  const [durationMin, setDurationMin] = useState(30)
  const [daysAgo, setDaysAgo] = useState(0)

  const startedAt = useMemo(() => {
    const date = new Date()
    date.setDate(date.getDate() - daysAgo)
    return date
  }, [daysAgo])

  const kcal = useMemo(
    () =>
      estimateKcal(
        {
          id: 'preview',
          type,
          durationMin,
          startedAt: startedAt.toISOString(),
          source: 'manual',
        },
        weightKg,
      ),
    [type, durationMin, startedAt, weightKg],
  )

  function submit() {
    addActivity({ type, durationMin, startedAt })
    navigate('/done', { replace: true })
  }

  return (
    <Screen>
      <ScreenBody className="safe-top pb-6">
        <header className="mb-5">
          <IconButton icon={ChevronLeft} label={t('common.back')} onClick={() => navigate(-1)} />
          <h1 className="text-display mt-6 font-bold">{t('activity.title')}</h1>
        </header>

        {/* Aperçu vivant de la séance : la photo suit l'activité choisie, le
            chiffre suit la durée. */}
        <div className="relative mb-8 h-40 overflow-hidden rounded-[var(--radius-card)] shadow-[var(--shadow-soft)]">
          <img
            key={type}
            src={photo(type)}
            alt=""
            className="animate-rise absolute inset-0 size-full object-cover"
          />
          <div className="from-night/75 absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t to-transparent" />
          <div className="text-surface absolute inset-x-5 bottom-4 flex items-end justify-between gap-3">
            <p className="text-caption font-semibold">{t(`intensity.${intensityOf(type)}`)}</p>
            <p className="text-title num font-bold">{t('activity.kcal', { count: kcal })}</p>
          </div>
        </div>

        <section className="mb-8">
          <Label>
            {ordered.favorites.length > 0 ? t('activity.favorites') : t('activity.typeTitle')}
          </Label>
          <ActivityTileGrid
            types={ordered.favorites}
            isSelected={(option) => option === type}
            onToggle={setType}
          />
          {ordered.rest.length > 0 && (
            <div className={ordered.favorites.length > 0 ? 'mt-5' : ''}>
              {ordered.favorites.length > 0 && <Label>{t('activity.others')}</Label>}
              <ActivityTileGrid
                types={ordered.rest}
                isSelected={(option) => option === type}
                onToggle={setType}
              />
            </div>
          )}
        </section>

        <section className="mb-8">
          <Label>{t('activity.durationTitle')}</Label>
          <div className="grid grid-cols-3 gap-2">
            {DURATIONS.map((option) => (
              <Pill
                key={option}
                selected={durationMin === option}
                onClick={() => setDurationMin(option)}
              >
                {t('common.minutes', { count: option })}
              </Pill>
            ))}
          </div>
        </section>

        <section>
          <Label>{t('activity.whenTitle')}</Label>
          <div className="grid grid-cols-3 gap-2">
            {WHEN.map((option) => (
              <Pill key={option} selected={daysAgo === option} onClick={() => setDaysAgo(option)}>
                {option === 0
                  ? t('common.today')
                  : option === 1
                    ? t('common.yesterday')
                    : t('common.daysAgo', { count: option })}
              </Pill>
            ))}
          </div>
        </section>
      </ScreenBody>

      <ScreenFooter safe>
        <Button full onClick={submit}>
          {t('common.save')}
        </Button>
      </ScreenFooter>
    </Screen>
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return <h2 className="text-body mb-3 font-bold">{children}</h2>
}
